/** The seven-step trail of one file (`trail.ts` is the model; this only draws it).
 *
 * An ordered list, because the steps are ordered and a screen reader should say "list, 7 items".
 * **A step's state is a word and a glyph, never a colour alone**: the glyph is decorative and the
 * state is in the accessible text (`done`, `in progress`, `waiting for a person`, `failed`, `not yet`).
 * The step a person should look at carries `aria-current="step"`.
 *
 * Motion is transform and opacity only, once, and off under `prefers-reduced-motion` (the CSS says
 * so; there is no script-driven animation here to turn off).
 */

import { Check, CircleNotch, Hourglass, X } from "@phosphor-icons/react";

import type { Trail, TrailStep } from "./trail";
import styles from "./UploadTrail.module.css";

const WORDS: Record<TrailStep["status"], string> = {
  done: "done",
  current: "in progress",
  waiting: "waiting for a person",
  failed: "failed",
  todo: "not yet",
};

function Glyph({ status }: { status: TrailStep["status"] }) {
  const common = { size: 12, weight: "bold" as const, "aria-hidden": true };
  if (status === "done") return <Check {...common} />;
  if (status === "current") return <CircleNotch {...common} className={styles.spin} />;
  if (status === "waiting") return <Hourglass {...common} />;
  if (status === "failed") return <X {...common} />;
  return null;
}

export function UploadTrail({
  trail,
  label,
  compact = false,
}: {
  trail: Trail;
  /** What the trail is of, for its accessible name: the file's name. */
  label: string;
  /** The table variant: the dots and a count, without the words under them. */
  compact?: boolean;
}) {
  return (
    <ol
      className={`${styles.trail} ${compact ? styles.compact : ""}`}
      aria-label={`Progress of ${label}`}
      data-testid="upload-trail"
      data-at={trail.at ?? "done"}
    >
      {trail.steps.map((step) => (
        <li
          key={step.key}
          className={`${styles.step} ${styles[step.status]}`}
          data-step={step.key}
          data-status={step.status}
          aria-current={step.key === trail.at ? "step" : undefined}
          title={compact ? `${step.label}: ${WORDS[step.status]}${step.detail ? `, ${step.detail}` : ""}` : undefined}
        >
          <span className={styles.dot} aria-hidden="true">
            <Glyph status={step.status} />
          </span>
          <span className={compact ? "visuallyHidden" : styles.words}>
            <span className={styles.label}>{step.label}</span>
            <span className="visuallyHidden">: {WORDS[step.status]}</span>
            {step.detail && !compact ? <span className={styles.detail}>{step.detail}</span> : null}
            {step.detail && compact ? <span className="visuallyHidden">, {step.detail}</span> : null}
          </span>
        </li>
      ))}
    </ol>
  );
}
